import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { Page, PageResult, ServiceHandler } from '@nucleus/ui-base';
import {
  BucketSummary,
  CreateReferenceDataItemPayload,
  ReferenceDataControllerService,
  ReferenceDataItem,
  ReferenceDataItemFilter,
  ReferenceDataItemReference,
  UpdateReferenceDataItemPayload
} from '@nucleus/reference-data-ui';
import { RequestHandler } from '../request-handler';

/**
 * Thin wrapper over the @nucleus/reference-data-ui embedded OpenAPI client
 * (ReferenceDataControllerService). Mirrors goldfish's ReferenceDataService
 * but uses TaskSense's own RequestHandler + an inline paginated-filter
 * request shape (TaskSense has no buildFilterRequest helper — see
 * FilterRequest / UserDataService.toApiFilterRequest for the same shape).
 */
@Injectable({ providedIn: 'root' })
export class ReferenceDataService {

  private api: ReferenceDataControllerService = inject(ReferenceDataControllerService);
  private requestHandler: RequestHandler = inject(RequestHandler);

  findAll(bucket: string, handler: ServiceHandler<ReferenceDataItem[]>): void {
    this.requestHandler.handleRequest(this.api.findAll(bucket) as unknown as Observable<ReferenceDataItem[]>, handler, false);
  }

  /** GLOBAL bucket browser: list every reference-data bucket (GET /v1/buckets). */
  findAllBuckets(handler: ServiceHandler<BucketSummary[]>): void {
    this.requestHandler.handleRequest(this.api.findAllBuckets() as unknown as Observable<BucketSummary[]>, handler, false);
  }

  findAllReferences(bucket: string, handler: ServiceHandler<ReferenceDataItemReference[]>): void {
    this.requestHandler.handleRequest(this.api.findAllByBucket(bucket) as unknown as Observable<ReferenceDataItemReference[]>, handler, false);
  }

  filter(bucket: string, handler: ServiceHandler<PageResult<ReferenceDataItem>>, filter: ReferenceDataItemFilter, page?: Page): void {
    this.requestHandler.handleRequest(
      this.api.filter(bucket, this.toRequest(filter, page) as any) as unknown as Observable<PageResult<ReferenceDataItem>>,
      handler, false);
  }

  getById(bucket: string, id: number, handler: ServiceHandler<ReferenceDataItem>): void {
    this.requestHandler.handleRequest(this.api.findById(bucket, id) as unknown as Observable<ReferenceDataItem>, handler, false);
  }

  create(bucket: string, payload: CreateReferenceDataItemPayload, handler: ServiceHandler<ReferenceDataItem>): void {
    this.requestHandler.handleRequest(this.api.create(bucket, payload) as unknown as Observable<ReferenceDataItem>, handler, true);
  }

  update(bucket: string, id: number, payload: UpdateReferenceDataItemPayload, handler: ServiceHandler<ReferenceDataItem>): void {
    this.requestHandler.handleRequest(this.api.update(bucket, id, payload) as unknown as Observable<ReferenceDataItem>, handler, true);
  }

  delete(bucket: string, id: number, handler: ServiceHandler<void>): void {
    this.requestHandler.handleRequest(this.api._delete(bucket, id) as unknown as Observable<void>, handler, true);
  }

  getPDFReport(bucket: string, handler: ServiceHandler<Blob>, filter: ReferenceDataItemFilter, page?: Page): void {
    this.requestHandler.handleRequest(this.api.createPdfReport(bucket, this.toRequest(filter, page) as any) as unknown as Observable<Blob>, handler, true);
  }

  getXLSReport(bucket: string, handler: ServiceHandler<Blob>, filter: ReferenceDataItemFilter, page?: Page): void {
    this.requestHandler.handleRequest(this.api.createExcelReport(bucket, this.toRequest(filter, page) as any) as unknown as Observable<Blob>, handler, true);
  }

  getCSVReport(bucket: string, handler: ServiceHandler<Blob>, filter: ReferenceDataItemFilter, page?: Page): void {
    this.requestHandler.handleRequest(this.api.createCSVReport(bucket, this.toRequest(filter, page) as any) as unknown as Observable<Blob>, handler, true);
  }

  /** Build the backend's PaginatedFilterRequest<ReferenceDataItemFilter> shape
   *  ({ page, filter }) — same contract UserDataService.toApiFilterRequest uses. */
  private toRequest(filter: ReferenceDataItemFilter, page?: Page): any {
    return {
      page: page ? {
        pageSize: page.pageSize,
        pageNumber: page.pageNumber,
        sortField: page.sortField,
        sortDirection: page.sortDirection as unknown as string,
      } : undefined,
      filter: { ...(filter as any) },
    };
  }
}
